import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import get from 'lodash.get';

import { decodeString } from 'lib/stringUtils';
import { isShellArticle as isShell } from 'lib/article';

import * as widgetTypes from './widgetTypes';

import './styles.themed.scss';

const WIDGET_SIZES = {
  SMALL: 'Small',
  EDGETOEDGE: 'edgetoedge',
};

/**
 * Checks if the given properties contain a menu embed.
 *
 * @param {object} properties - The properties object to check.
 * @returns {boolean} - Returns true if a menu embed is found, otherwise false.
 */
const hasMenuEmbed = (properties) => {
  const decodedHtml = decodeString(get(properties, '[\'embed-code\']', '')) || '';
  return !!decodedHtml.match(/class=["“”]menu-embed["“”]/);
};

/**
 * Maps the state from the Redux store to the props for the component.
 *
 * @param {object} state - The Redux state object.
 * @param {object} state.article - The article state.
 * @param {object} state.shared - The shared state.
 * @param {object} state.router - The router state.
 * @returns {object} - The mapped props.
 */
const mapStateToProps = ({ article, shared, router }) => ({
  url: article.content?.[0]?.url,
  vertical: shared.vertical,
  domain: router.domain,
  isShellArticle: isShell(article),
});

/**
 * EmbedWidget component renders an embedded widget based on the provided props.
 *
 * @param {object} props - The properties passed to the component.
 * @param {string|null} [props.elementId=null] - The ID of the element for anchoring.
 * @param {import('lib/graphqlTypeDefs.mjs').Presentation} props.presentation - Presentation details for the widget.
 * @param {import('lib/graphqlTypeDefs.mjs').Widget} props.widget - The widget data to render.
 * @param {boolean} [props.insertEndmark=false] - Whether to insert an endmark.
 * @param {boolean} [props.isShellArticle=false] - Whether the article is a shell article.
 * @param {import('lib/graphqlTypeDefs.mjs').Url} props.url - The URL object containing canonical information.
 * @returns {JSX.Element|null} - The rendered widget or null if no widget type is found.
 */
const EmbedWidget = (props) => {
  const {
    elementId = null,
    presentation,
    widget,
    insertEndmark = false,
    isShellArticle = false,
    url,
  } = props;
  const { size } = presentation;
  const {
    baseline: html,
    name,
    properties = {},
    publisher: { name: vertical } = {},
  } = widget;

  let getWidgetType = widgetTypes[name];

  if (!getWidgetType) {
    if (html && vertical === 'today') {
      getWidgetType = widgetTypes.todayLegacyWidget;
    } else if (properties?.embed?.type === 'ALL_PURPOSE_EMBED') {
      getWidgetType = widgetTypes.advanced_embed;
    } else {
      return null;
    }
  }

  const isSports = url.canonical.includes('/sports');

  const containerClasses = classNames('embed-widget', 'embedWidget', {
    'embed-widget__small-widget': size === WIDGET_SIZES.SMALL,
    'embed-widget__twitter': name === 'tweet_embed' && size === WIDGET_SIZES.SMALL,
    'embed-widget__youtube': name === 'youtubeplus',
    'embed-widget__opinary': name === 'opinary',
    'embed-widget__vimeo': name === 'vimeo',
    'embed-widget__pinterest': /pinterest/.test(name),
    'embedded-endmark': insertEndmark,
    'embed-widget--shell-article': isShellArticle,
    'embed-widget--scroll-table': isSports,
  }, `embed-widget--${size}`);

  const widgetContainsMenuEmbed = name === 'advanced_embed' && html && hasMenuEmbed(properties);

  const widgetClassName = widgetContainsMenuEmbed
    ? 'embed-widget__menu'
    : containerClasses;

  return (
    <section
      id={elementId && `anchor-${elementId}`}
      className={widgetClassName}
      data-testid="embed-widget"
    >
      {getWidgetType(props)}
    </section>
  );
};

export const EmbedWidgetConnected = connect(mapStateToProps)(EmbedWidget);
